import { useEffect } from 'react';
import { Button, Card, Space, Table, Tag, Typography, message } from 'antd';
import type { PacketPrintJob } from '../../redux/api/traveler-audit';
import {
	useConfirmPrintJobMutation,
	useGetPrintJobsQuery,
	useLazyGetPrintJobPdfQuery,
	useLazyGetPrintJobsQuery,
	useReprintPrintJobMutation,
	useRetryPrintJobMutation,
} from '../../redux/api/traveler-audit';

import { getApiErrorMessage } from '../../utils/apiErrorMessage';

const { Text } = Typography;

/** Force-refetch while PDFs are still building. Same reason as Live ERP:
 * RTK's pollingInterval can keep serving a cached queued row. */
const PRINT_POLL_MS = 2500;

const isBuilding = (job: PacketPrintJob) =>
	job.status === 'queued' && job.page_count === 0 && !job.error;

/** A build that never landed (worker restart, purged queue) leaves the row
 * queued forever. Without this it renders as "building" with every action
 * disabled and no Retry, so an operator has no way back. */
const STALLED_AFTER_MS = 10 * 60 * 1000;
const STALLED_NEXT =
	'Retry this job. If it stays queued, check that the Celery worker is running.';

const jobNextStep = (job: PacketPrintJob) =>
	job.error_details?.next_step || (isStalled(job) ? STALLED_NEXT : '');

const isStalled = (job: PacketPrintJob) =>
	isBuilding(job) &&
	Date.now() - new Date(job.modified_time || job.created_time).getTime() >
		STALLED_AFTER_MS;

const statusColor = (s: string) => {
	if (s === 'printed') return 'green';
	if (s === 'reprint') return 'gold';
	if (s === 'failed') return 'red';
	return 'blue';
};

const downloadBlob = (blob: Blob, filename: string) => {
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
	setTimeout(() => URL.revokeObjectURL(url), 60000);
};

export function PrintQueue({ runId }: { runId?: string }) {
	const { data, isFetching, isLoading, refetch } = useGetPrintJobsQuery(runId);
	const [loadJobs] = useLazyGetPrintJobsQuery();
	const [confirm, { isLoading: isConfirming }] = useConfirmPrintJobMutation();
	const [reprint, { isLoading: isReprinting }] = useReprintPrintJobMutation();
	const [retry, { isLoading: isRetrying }] = useRetryPrintJobMutation();
	const [loadPdf] = useLazyGetPrintJobPdfQuery();

	const building = (data ?? []).some((j) => isBuilding(j) && !isStalled(j));

	useEffect(() => {
		if (!building) return;

		let cancelled = false;
		let timeoutId = 0;

		const tick = async () => {
			if (cancelled) return;
			try {
				// preferCacheValue=false forces a network read of the queue.
				await loadJobs(runId, false).unwrap();
			} catch {
				// Transient API blip — keep polling.
			}
			if (cancelled) return;
			timeoutId = window.setTimeout(() => {
				void tick();
			}, PRINT_POLL_MS);
		};

		void tick();
		return () => {
			cancelled = true;
			window.clearTimeout(timeoutId);
		};
	}, [building, runId, loadJobs]);

	const handlePdf = async (job: PacketPrintJob) => {
		try {
			const blob = await loadPdf(job.id).unwrap();
			downloadBlob(blob, `packet_print_${job.id}.pdf`);
		} catch {
			message.error('Could not download the archived PDF.');
		}
	};

	return (
		<Card
			title="Print queue"
			style={{ marginTop: 16 }}
			extra={
				<Button size="small" loading={isFetching} onClick={() => void refetch()}>
					Refresh
				</Button>
			}
		>
			<Text type="secondary">
				Jobs appear as queued immediately; packet PDFs build in the background. Whole
				packets are packed into ~50-page jobs and never split. Confirm after the
				printer finishes; reprint uses the archived PDF. Retry rebuilds a failed or stalled job.
			</Text>
			<Table
				style={{ marginTop: 12 }}
				size="small"
				rowKey="id"
				loading={isLoading}
				dataSource={data ?? []}
				pagination={{ pageSize: 10 }}
				columns={[
					{
						title: 'Created',
						dataIndex: 'created_time',
						render: (v: string) => new Date(v).toLocaleString(),
					},
					{
						title: 'Status',
						dataIndex: 'status',
						width: 110,
						render: (s: string, job: PacketPrintJob) => {
							if (isStalled(job)) return <Tag color="red">stalled</Tag>;
							const label = isBuilding(job) ? 'building' : s;
							return <Tag color={statusColor(s)}>{label}</Tag>;
						},
					},
					{ title: 'Pages', dataIndex: 'page_count', width: 80 },
					{
						title: 'Packets',
						render: (_v, job) => job.line_item_ids.length,
						width: 80,
					},
					{
						title: 'Error',
						render: (_v, job) => {
							const next = jobNextStep(job);
							const text =
								job.error || (isStalled(job) ? 'Build never landed' : '—');
							return (
								<div>
									<div>{text}</div>
									{next ? <Text type="secondary">{next}</Text> : null}
								</div>
							);
						},
					},
					{
						title: 'Confirmed',
						render: (_v, job) =>
							job.confirmed_at ? new Date(job.confirmed_at).toLocaleString() : '—',
					},
					{
						title: '',
						width: 320,
						render: (_v, job) => (
							<Space size={4}>
								<Button
									size="small"
									disabled={job.status === 'failed' || isBuilding(job)}
									onClick={() => void handlePdf(job)}
								>
									PDF
								</Button>
								<Button
									size="small"
									disabled={isBuilding(job)}
									onClick={async () => {
										try {
											await confirm(job.id).unwrap();
											message.success('Print confirmed.');
										} catch (err) {
											message.error(
												getApiErrorMessage(err, 'Could not confirm print.')
											);
										}
									}}
									loading={isConfirming}
								>
									Confirm
								</Button>
								{job.status === 'failed' || isStalled(job) ? (
									<Button
										size="small"
										onClick={async () => {
											try {
												const rebuilt = await retry(job.id).unwrap();
												if (rebuilt.status === 'failed') {
													const next = rebuilt.error_details?.next_step;
													message.error(
														[
															rebuilt.error ||
																'Could not rebuild the packet. Check the error and retry.',
															next,
														]
															.filter(Boolean)
															.join(' ')
													);
												} else {
													message.success('Print job rebuilt and queued.');
												}
											} catch (err) {
												message.error(
													getApiErrorMessage(err, 'Could not retry the print job.')
												);
											}
										}}
										loading={isRetrying}
									>
										Retry
									</Button>
								) : (
									<Button
										size="small"
										disabled={isBuilding(job)}
										onClick={async () => {
											try {
												await reprint(job.id).unwrap();
												message.success('Reprint queued from the archive.');
											} catch (err) {
												message.error(
													getApiErrorMessage(err, 'Could not queue a reprint.')
												);
											}
										}}
										loading={isReprinting}
									>
										Reprint
									</Button>
								)}
							</Space>
						),
					},
				]}
			/>
		</Card>
	);
}
